import { useState, useMemo } from "react";
import type {
  Question,
  QuizFilter,
  StudyRecord,
  QuestionTracker,
  LessonUnit,
  StudyPathway,
  LastLesson,
} from "@shikaku/shared";
import { buildLessonStructure, countQuestionsInUnit } from "@shikaku/shared";

interface HomeViewProps {
  questions: Question[];
  record: StudyRecord;
  tracker: QuestionTracker;
  primaryColor: string;
  usesUnitModeSelection: boolean;
  studyPathways: StudyPathway[];
  onStartQuiz: (filter: QuizFilter, unit?: string, subunit?: string, difficulty?: number) => void;
  onStartMemorize: (filter: QuizFilter, unit?: string, subunit?: string, difficulty?: number) => void;
}

function difficultyLabel(d: number): string {
  return d === 1 ? "基礎" : d === 2 ? "標準" : "発展";
}

export function HomeView({
  questions,
  record,
  tracker,
  primaryColor,
  usesUnitModeSelection,
  studyPathways,
  onStartQuiz,
  onStartMemorize,
}: HomeViewProps) {
  const [drillUnit, setDrillUnit] = useState<string | null>(null);
  const [drillSubunit, setDrillSubunit] = useState<string | null>(null);
  const [drillDifficulty, setDrillDifficulty] = useState<number | null>(null);
  const [selectedPathwayId, setSelectedPathwayId] = useState<string | null>(null);

  // Checkbox filters
  const [filterUnseen, setFilterUnseen] = useState(true);
  const [filterWrong, setFilterWrong] = useState(true);
  const [filterCorrect, setFilterCorrect] = useState(false);
  const [filterBookmarked, setFilterBookmarked] = useState(false);

  const lessonStructure = buildLessonStructure(questions);
  const activePathway = studyPathways.find((p) => p.id === selectedPathwayId);
  const visibleStructure = activePathway
    ? lessonStructure.filter((u) => activePathway.units.includes(u.unit))
    : lessonStructure;

  // Compute stats
  const stats = useMemo(() => {
    const total = questions.length;
    const answeredIds = new Set<string>();
    tracker.correctIDs.forEach((id) => answeredIds.add(id));
    tracker.wrongIDs.forEach((id) => answeredIds.add(id));
    const unseen = total - answeredIds.size;
    return {
      total,
      unseen,
      wrong: tracker.wrongIDs.size,
      correct: tracker.correctIDs.size,
      bookmarked: tracker.bookmarkedIDs.size,
    };
  }, [questions, tracker]);

  const barTotal = stats.total || 1;

  const resumeLastLesson = (last: LastLesson) => {
    const base = last.mode.split(":")[0];
    if (base === "memorize") {
      onStartMemorize("all", last.unit, last.subunit, last.difficulty);
    } else {
      onStartQuiz("all", last.unit, last.subunit, last.difficulty);
    }
  };

  // ─── Mode Selection (after unit drill) ────────
  if (drillUnit && (usesUnitModeSelection || (drillSubunit && drillDifficulty))) {
    const unit = drillUnit;
    const subunit = usesUnitModeSelection ? undefined : drillSubunit ?? undefined;
    const diff = usesUnitModeSelection ? undefined : drillDifficulty ?? undefined;
    const title = usesUnitModeSelection
      ? unit
      : `${subunit ?? unit}${diff ? ` - ${difficultyLabel(diff)}` : ""}`;

    return (
      <div>
        <div style={{ padding: "16px 20px", background: "#fff", borderBottom: "1px solid #e8e8e8" }}>
          <button
            onClick={() => {
              if (usesUnitModeSelection) setDrillUnit(null);
              else { setDrillSubunit(null); setDrillDifficulty(null); }
            }}
            style={{ color: primaryColor, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}
          >
            ← 戻る
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 8, color: "#333" }}>{title}</div>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <button className="btn-primary" onClick={() => onStartQuiz("all", unit, subunit, diff)}>
            テスト
          </button>
          <button className="btn-primary" style={{ background: "linear-gradient(180deg, #b388ff, #9c5ee0)" }} onClick={() => onStartMemorize("all", unit, subunit, diff)}>
            暗記モード
          </button>
        </div>
      </div>
    );
  }

  // ─── Subunit & difficulty drill ───────────────
  if (drillUnit && !drillSubunit) {
    const entry = lessonStructure.find((u) => u.unit === drillUnit);
    if (!entry) return null;
    return (
      <div>
        <div style={{ padding: "16px 20px", background: "#fff", borderBottom: "1px solid #e8e8e8" }}>
          <button
            onClick={() => setDrillUnit(null)}
            style={{ color: primaryColor, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}
          >
            ← ホーム
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 8, color: "#333" }}>{drillUnit}</div>
        </div>

        <div style={{ background: "#fff", marginTop: 0 }}>
          {entry.lessons.map((lesson, li) => (
            <div key={lesson.subunit}>
              <div style={{ padding: "12px 20px 6px", fontSize: 13, fontWeight: 700, color: "#888" }}>
                {lesson.subunit}
              </div>
              {lesson.levels.map((level) => {
                const correct = level.ids.filter((id) => tracker.correctIDs.has(id)).length;
                const total = level.ids.length;
                const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
                return (
                  <button
                    key={level.difficulty}
                    className="list-row"
                    onClick={() => { setDrillSubunit(lesson.subunit); setDrillDifficulty(level.difficulty); }}
                  >
                    <div>
                      <span style={{ fontSize: 14, color: "#333" }}>
                        {"★".repeat(level.difficulty)} {difficultyLabel(level.difficulty)}
                      </span>
                      <span style={{ fontSize: 13, color: "#999", marginLeft: 12 }}>{total}問</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 60, height: 6, background: "#e8e8e8", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: primaryColor, borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: pct === 100 ? "#2ecc71" : "#888", width: 36, textAlign: "right" }}>
                        {pct}%
                      </span>
                      <svg width="16" height="16" fill="none" stroke="#ccc" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                );
              })}
              {li < entry.lessons.length - 1 && <div style={{ margin: "0 20px", borderTop: "1px solid #f0f0f0" }} />}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Pathway unit list ────────────────────────
  if (selectedPathwayId && activePathway) {
    return (
      <div>
        <div style={{ padding: "16px 20px", background: "#fff", borderBottom: "1px solid #e8e8e8" }}>
          <button
            onClick={() => setSelectedPathwayId(null)}
            style={{ color: primaryColor, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}
          >
            ← ホーム
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 8, color: "#333" }}>{activePathway.title}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{activePathway.subtitle}</div>
        </div>
        <UnitListSection
          units={visibleStructure}
          onSelect={(unit) => {
            setDrillUnit(unit);
            if (usesUnitModeSelection) { setDrillSubunit(null); setDrillDifficulty(null); }
          }}
        />
      </div>
    );
  }

  // ─── Main Dashboard ───────────────────────────
  return (
    <div>
      {/* Stats Section */}
      <div style={{ background: "#fff", padding: "24px 20px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <StatBox label="未出題" value={stats.unseen} color="#1abac4" />
          <StatBox label="ミス" value={stats.wrong} color="#ff5a7e" />
          <StatBox label="正解" value={stats.correct} color="#2ecc71" />
          <StatBox label="ブックマーク" value={stats.bookmarked} color="#f0a030" />
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 20 }}>
          <div className="progress-bar-container">
            <div
              className="segment"
              style={{ width: `${(stats.correct / barTotal) * 100}%`, background: "#2ecc71" }}
            />
            <div
              className="segment"
              style={{ width: `${(stats.wrong / barTotal) * 100}%`, background: "#ff5a7e" }}
            />
          </div>
        </div>
      </div>

      {/* Last lesson */}
      {record.lastLesson && (
        <div style={{ background: "#f0f0f0", padding: "16px 20px" }}>
          <LastLessonSection
            last={record.lastLesson}
            onResume={() => resumeLastLesson(record.lastLesson!)}
            onRetryWrong={() => onStartQuiz("wrongOnly")}
            wrongCount={stats.wrong}
          />
        </div>
      )}

      {/* Filter checkboxes */}
      <div style={{ background: "#fff", padding: "16px 20px", borderBottom: "1px solid #e8e8e8" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#555" }}>出題対象</span>
        </div>
        <div className="checkbox-group">
          <label className="checkbox-item">
            <input type="checkbox" checked={filterUnseen} onChange={(e) => setFilterUnseen(e.target.checked)} />
            未出題
          </label>
          <label className="checkbox-item">
            <input type="checkbox" checked={filterWrong} onChange={(e) => setFilterWrong(e.target.checked)} />
            ミス
          </label>
          <label className="checkbox-item">
            <input type="checkbox" checked={filterCorrect} onChange={(e) => setFilterCorrect(e.target.checked)} />
            正解済み
          </label>
          <label className="checkbox-item">
            <input type="checkbox" checked={filterBookmarked} onChange={(e) => setFilterBookmarked(e.target.checked)} />
            ブックマーク
          </label>
        </div>
      </div>

      {/* Pathway filter */}
      {studyPathways.length > 0 && (
        <div style={{ background: "#fff", padding: "12px 20px", borderBottom: "1px solid #e8e8e8" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#555", marginRight: 4 }}>コース</span>
            <PathwayChip
              label="すべて"
              active={selectedPathwayId === null}
              onClick={() => setSelectedPathwayId(null)}
              primaryColor={primaryColor}
            />
            {studyPathways.map((p) => (
              <PathwayChip
                key={p.id}
                label={p.title}
                active={selectedPathwayId === p.id}
                onClick={() => setSelectedPathwayId(p.id === selectedPathwayId ? null : p.id)}
                primaryColor={primaryColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Unit list */}
      <UnitListSection
        units={visibleStructure}
        onSelect={(unit) => {
          setDrillUnit(unit);
          if (usesUnitModeSelection) { setDrillSubunit(null); setDrillDifficulty(null); }
        }}
      />

      {/* Start all button */}
      <div style={{ padding: "16px 20px 32px" }}>
        <button
          className="btn-primary"
          onClick={() => {
            if (filterBookmarked && !filterUnseen && !filterWrong && !filterCorrect) {
              onStartQuiz("bookmarked");
            } else if (filterWrong && !filterUnseen && !filterCorrect && !filterBookmarked) {
              onStartQuiz("wrongOnly");
            } else {
              onStartQuiz("all");
            }
          }}
        >
          演習スタート
        </button>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="stat-box">
      <div className="stat-label" style={{ color }}>{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function PathwayChip({
  label, active, onClick, primaryColor,
}: { label: string; active: boolean; onClick: () => void; primaryColor: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px",
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        borderRadius: 20,
        border: active ? `2px solid ${primaryColor}` : "2px solid #ddd",
        background: active ? `${primaryColor}10` : "#fff",
        color: active ? primaryColor : "#666",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

function LastLessonSection({
  last, onResume, onRetryWrong, wrongCount,
}: {
  last: LastLesson; onResume: () => void; onRetryWrong: () => void; wrongCount: number;
}) {
  const base = last.mode.split(":")[0];
  const modeNames: Record<string, string> = { quiz: "自由演習", memorize: "暗記" };
  const modeName = modeNames[base] ?? last.mode;

  let detail = modeName;
  if (last.unit) detail = `${modeName} - ${last.unit}`;

  return (
    <div>
      <div style={{ textAlign: "center", fontSize: 14, color: "#666", marginBottom: 12 }}>
        前回の演習：{detail}
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button className="btn-primary" style={{ flex: 1 }} onClick={onResume}>
          続きから
        </button>
        {wrongCount > 0 && (
          <button className="btn-primary" style={{ flex: 1, background: "linear-gradient(180deg, #ff7eb3, #ff5a7e)" }} onClick={onRetryWrong}>
            ミスに再挑戦
          </button>
        )}
      </div>
    </div>
  );
}

function UnitListSection({ units, onSelect }: { units: LessonUnit[]; onSelect: (unit: string) => void }) {
  return (
    <div style={{ background: "#fff", marginTop: 0 }}>
      {units.map((entry, i) => {
        const total = countQuestionsInUnit(entry);
        return (
          <div key={entry.unit}>
            <button
              className="list-row"
              onClick={() => onSelect(entry.unit)}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#333" }}>{entry.unit}</div>
                <div style={{ fontSize: 13, color: "#999", marginTop: 2 }}>{total}問</div>
              </div>
              <svg width="16" height="16" fill="none" stroke="#ccc" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {i < units.length - 1 && <div style={{ margin: "0 20px", borderTop: "1px solid #f0f0f0" }} />}
          </div>
        );
      })}
    </div>
  );
}
